import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IExtractedNote } from '../../models/quotations.models';

interface IEditableNote extends IExtractedNote {
  /** true once the user has manually changed this note's text (drives the amber border + badge). */
  edited?: boolean;
  /** true while the inline editor is open for this note. */
  editing?: boolean;
  /** soft-removed: kept visible (struck-through) but excluded from the saved payload, so it is not sent to the ERP. */
  removed?: boolean;
  /** working text while editing, before confirm. */
  draft?: string;
}

/**
 * Inline editor for AI extracted notes (WB-6475). The AI summary is rendered read-only;
 * only the categorized notes can be edited or removed. Removing is a soft action (the note
 * stays struck-through with a revert option). Edits/removals auto-save by emitting the full
 * desired state (removed notes excluded), which the host dispatches via UpdateEmailOrder (WB-6681).
 */
@Component({
  selector: 'app-extracted-notes-editor',
  templateUrl: './extracted-notes-editor.component.html',
  styleUrls: ['./extracted-notes-editor.component.scss'],
})
export class ExtractedNotesEditorComponent implements OnChanges {
  @Input() notes: { extracted: IExtractedNote[]; summary: string } | null = null;
  @Input() editable = false;

  /** Emits the full desired state of the extracted notes (including soft-deleted ones with is_deleted: true) after every change. */
  @Output() notesSave = new EventEmitter<IExtractedNote[]>();

  summary = '';
  workingNotes: IEditableNote[] = [];

  // Snapshot of the last array we are in sync with (input or what we just emitted),
  // so the echo from an auto-save refresh does not reset local state (edited/removed flags).
  private lastSyncedSnapshot = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['notes']) return;
    const incoming = this.notes?.extracted ?? [];
    const snapshot = JSON.stringify(
      incoming.map((n) => ({ category: n.category, text: n.text, is_deleted: n.is_deleted ?? false }))
    );
    this.summary = this.notes?.summary ?? '';
    if (snapshot !== this.lastSyncedSnapshot) {
      this.workingNotes = incoming.map((note) => ({
        ...note,
        edited: note.is_edited ?? false,
        removed: note.is_deleted ?? false,
      }));
      this.lastSyncedSnapshot = snapshot;
    }
  }

  startEdit(note: IEditableNote): void {
    if (!this.editable || note.editing || note.removed) return;
    note.draft = note.text;
    note.editing = true;
  }

  confirmEdit(note: IEditableNote): void {
    const draft = (note.draft ?? '').trim();
    if (!draft) {
      // Clearing the text is treated as a removal.
      note.editing = false;
      note.draft = undefined;
      this.discard(note);
      return;
    }
    if (draft !== note.text) {
      note.text = draft;
      note.edited = true;
    }
    note.editing = false;
    note.draft = undefined;
    this.emitSave();
  }

  /** Enter confirms the edit; Shift+Enter inserts a newline. */
  onEnter(event: Event, note: IEditableNote): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.confirmEdit(note);
  }

  cancelEdit(note: IEditableNote): void {
    note.editing = false;
    note.draft = undefined;
  }

  discard(note: IEditableNote): void {
    if (!this.editable) return;
    note.removed = true;
    note.editing = false;
    note.draft = undefined;
    this.emitSave();
  }

  revert(note: IEditableNote): void {
    if (!this.editable) return;
    note.removed = false;
    this.emitSave();
  }

  private emitSave(): void {
    const payload: IExtractedNote[] = this.workingNotes.map(({ category, text, removed }) => ({
      category,
      text,
      is_deleted: removed ?? false,
    }));
    this.lastSyncedSnapshot = JSON.stringify(
      payload.map((n) => ({ category: n.category, text: n.text, is_deleted: n.is_deleted }))
    );
    this.notesSave.emit(payload);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
